import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { useAuthUser } from "../../../stores/authStore";

/**
 * O filtro de produto do Kanban guardado no perfil (06/10/2026): vale no computador e no
 * celular até a pessoa mudar. Um por cliente, porque os produtos são do contrato dele — a
 * equipe que troca de cliente encontra o filtro que deixou em cada um.
 *
 * Sem a rota (API antiga) ou sem rede, o filtro só não é lembrado; a tela segue normal.
 */
/**
 * A preferência do filtro, do cliente atual. Uma consulta só para o Kanban e o novo chamado.
 * A equipe guarda uma por contrato (09/10/2026): os dois painéis da T4i têm produtos diferentes.
 */
const usePreferenciaDoFiltro = () => {
  const usuario = useAuthUser();
  const chave = usuario?.idContrato
    ? `kanban.produtos.contrato.${usuario.idContrato}`
    : usuario?.idCliente
      ? `kanban.produtos.${usuario.idCliente}`
      : null;
  const salvo = useQuery({
    queryKey: ["preferencia", chave],
    queryFn: async () => {
      const { data } = await api.get<{ valor: string | null }>(`/preferencias/${chave}`);
      return data.valor;
    },
    enabled: chave !== null,
    retry: false,
    staleTime: Infinity,
  });
  return { chave, salvo };
};

/** Os produtos marcados no filtro do Kanban, só para ler (o novo chamado sugere o único). */
export function useProdutosDoFiltro(): string[] {
  const { salvo } = usePreferenciaDoFiltro();
  return useMemo(() => lerLista(salvo.data), [salvo.data]);
}

export function useProdutosSalvos(produtos: string[], aoCarregar: (produtos: string[]) => void) {
  const { chave, salvo } = usePreferenciaDoFiltro();
  const queryClient = useQueryClient();

  // Carrega uma vez por chave; depois disso, quem manda é a tela.
  const carregadoPara = useRef<string | null>(null);
  const ultimoGravado = useRef<string>("[]");
  const aoCarregarRef = useRef(aoCarregar);
  aoCarregarRef.current = aoCarregar;

  useEffect(() => {
    if (!chave || carregadoPara.current === chave) return;
    if (salvo.isError) {
      carregadoPara.current = chave;
      return;
    }
    if (!salvo.isSuccess) return;
    carregadoPara.current = chave;
    const lista = lerLista(salvo.data);
    ultimoGravado.current = JSON.stringify(lista);
    aoCarregarRef.current(lista);
  }, [chave, salvo.isSuccess, salvo.isError, salvo.data]);

  useEffect(() => {
    if (!chave || carregadoPara.current !== chave || salvo.isError) return;
    const atual = JSON.stringify(produtos);
    if (atual === ultimoGravado.current) return;
    ultimoGravado.current = atual;
    const valor = produtos.length > 0 ? atual : null;
    queryClient.setQueryData(["preferencia", chave], valor);
    api.put(`/preferencias/${chave}`, { valor }).catch(() => undefined);
  }, [chave, produtos, salvo.isError, queryClient]);
}

const lerLista = (valor: string | null | undefined): string[] => {
  if (!valor) return [];
  try {
    const lista: unknown = JSON.parse(valor);
    return Array.isArray(lista) ? lista.filter((p): p is string => typeof p === "string") : [];
  } catch {
    return [];
  }
};
